import { useEffect, useState } from "react";
import { toast } from "sonner";
import { MessageCircle, Send, BellRing, Mail } from "lucide-react";
import { api, errMsg, money, fmtDate } from "../lib/api";
import { Avatar, Spinner } from "./bits";

export function RemindersPanel({ group, tick }) {
  const [rows, setRows] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = () => api.get(`/groups/${group.id}/reminders`).then((r) => setRows(r.data)).catch(() => setRows([]));
  useEffect(() => { load(); }, [group.id, tick]); // eslint-disable-line react-hooks/exhaustive-deps
  const sendAll = async (ids) => {
    setBusy(true);
    try { const { data } = await api.post(`/groups/${group.id}/reminders/send`, ids || null); toast.success(`Reminders sent to ${data.sent} member${data.sent === 1 ? "" : "s"}`, { description: "In-app notification and email delivered." }); load(); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const waLink = (r) => `https://wa.me/${r.whatsapp_number || ""}?text=${encodeURIComponent(r.message)}`;
  if (!rows) return <Spinner />;
  if (rows.length === 0) return null;
  return (
    <div className="card p-5 md:p-6" data-testid="reminders-panel">
      <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
        <div><div className="font-semibold text-nini-heading inline-flex items-center gap-2"><BellRing size={17} className="text-nini-deep" /> Nudge outstanding members</div><p className="text-sm text-nini-muted mt-1">Send a friendly pre-filled WhatsApp message with one tap, or send everyone an in-app + email reminder. Automatic reminders also go out 3 days before and on the due date.</p></div>
        <button className="btn-primary" data-testid="send-all-reminders-btn" disabled={busy} onClick={() => sendAll()}><Send size={16} /> Remind all ({rows.length})</button>
      </div>
      <div className="divide-y divide-nini-border">
        {rows.map((r) => (
          <div key={r.user_id} className="flex flex-col sm:flex-row sm:items-center gap-3 py-3" data-testid={`reminder-row-${r.user_id}`}>
            <Avatar src={r.avatar_url} name={r.name} />
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold text-nini-heading">{r.name} <span className="text-nini-muted font-normal">· {money(r.total_outstanding)} outstanding</span></div>
              <div className="text-xs text-nini-muted truncate">{r.items.map((i) => `${i.ref_name} (due ${fmtDate(i.due_date)})`).join(" · ")}</div>
              {r.items[0]?.last_reminded_at && <div className="text-[11px] text-nini-muted">Last reminded {fmtDate(r.items[0].last_reminded_at, true)}</div>}
            </div>
            <div className="flex gap-2 shrink-0">
              <button className="btn-secondary !py-2 !px-3" data-testid={`remind-email-${r.user_id}`} disabled={busy} onClick={() => sendAll([r.user_id])} title="Send in-app + email reminder"><Mail size={15} /></button>
              {r.whatsapp_number ? (
                <a href={waLink(r)} target="_blank" rel="noreferrer" data-testid={`whatsapp-btn-${r.user_id}`} className="inline-flex items-center gap-2 bg-[#25D366] hover:bg-[#1ebe5d] text-white text-sm font-semibold px-4 py-2 rounded-xl transition"><MessageCircle size={16} /> WhatsApp</a>
              ) : <span className="text-xs text-nini-muted self-center">No mobile number</span>}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
